package com.flowyride.feature.navigation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.flowyride.core.device.NavigationDevice
import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.ManeuverType
import com.flowyride.core.domain.model.RoutePreferences
import com.flowyride.core.domain.model.RouteRequest
import com.flowyride.core.domain.model.RouteStyle
import com.flowyride.core.domain.navigation.ActiveNavigation
import com.flowyride.core.domain.navigation.ConnectorKind
import com.flowyride.core.domain.navigation.LocationFix
import com.flowyride.core.domain.navigation.NavigationEvent
import com.flowyride.core.domain.navigation.NavigationPreferences
import com.flowyride.core.domain.navigation.NavigationPhase
import com.flowyride.core.domain.navigation.NavigationRequest
import com.flowyride.core.domain.navigation.NavigationSession
import com.flowyride.core.domain.navigation.NavigationSnapshot
import com.flowyride.core.domain.navigation.RideSimulator
import com.flowyride.core.domain.routing.RouteEngine
import com.flowyride.core.domain.routing.TrackGuidance
import com.flowyride.core.domain.util.suspendRunCatching
import com.flowyride.feature.navigation.resources.Res
import com.flowyride.feature.navigation.resources.guidance_unavailable
import com.flowyride.feature.navigation.resources.stop_reached
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.math.roundToInt

/**
 * Runs the [ActiveNavigation]: feeds GPS fixes to a [NavigationSession], computes the ways back when asked,
 * speaks the camera alerts, keeps the Android service's notification up to date.
 *
 * @param engine computes connectors (to the departure, back onto the ride).
 * @param guidance turn-by-turn instructions for imported lines, which have none.
 * @param now wall clock, for the ride summary.
 */
class NavigationViewModel(
    private val activeNavigation: ActiveNavigation,
    private val device: NavigationDevice,
    private val engine: RouteEngine,
    private val guidance: TrackGuidance,
    private val settings: NavigationPreferences,
    private val now: () -> Long,
) : ViewModel() {

    private val request: NavigationRequest? = activeNavigation.request.value

    private val _state = MutableStateFlow(
        NavigationUiState(
            name = request?.name.orEmpty(),
            track = request?.route?.track?.map { it.point }.orEmpty(),
            stops = request?.route?.let { route -> route.legs.dropLast(1).map { route.track[it.lastTrackIndex].point } }.orEmpty(),
            simulated = request?.simulated == true,
            speedCameraAlerts = settings.speedCameraAlerts.value,
        ),
    )
    val state: StateFlow<NavigationUiState> = _state.asStateFlow()

    private val _events = Channel<NavigationUiEvent>(Channel.BUFFERED)
    val events: Flow<NavigationUiEvent> = _events.receiveAsFlow()

    private var session: NavigationSession? = null
    private var ridingJob: Job? = null
    private var connectorJob: Job? = null
    private var startedAt: Long? = null
    private var ridden = 0.0
    private var lastRidden: GeoPoint? = null
    private var lastNotification: String? = null
    private var lastFix: LocationFix? = null
    /** Since when the rider rides on while the map does not follow them. */
    private var movingAwaySince: Long? = null

    init {
        viewModelScope.launch { device.stopRequests.collect { stop() } }
        if (request?.simulated == true) start()
    }

    fun onAction(action: NavigationAction) {
        when (action) {
            NavigationAction.ZoomIn -> _state.update { it.copy(zoomOffset = (it.zoomOffset + ZOOM_STEP).coerceAtMost(MAX_ZOOM_OFFSET)) }
            NavigationAction.ZoomOut -> _state.update { it.copy(zoomOffset = (it.zoomOffset - ZOOM_STEP).coerceAtLeast(-MAX_ZOOM_OFFSET)) }
            NavigationAction.Stop -> stop()
            NavigationAction.ToggleOverview -> _state.update { it.copy(overview = !it.overview, mapMovedByHand = false) }
            NavigationAction.MapMoved -> _state.update { it.copy(mapMovedByHand = true) }
            NavigationAction.Recenter -> recenter()
            NavigationAction.LocationDenied -> _state.update { it.copy(locationDenied = true) }
            NavigationAction.LocationGranted -> {
                _state.update { it.copy(locationDenied = false) }
                start()
            }
        }
    }

    private fun start() {
        val request = request?.takeIf { it.route.track.size >= 2 } ?: return
        if (ridingJob != null) return
        ridingJob = viewModelScope.launch {
            var route = request.route
            // An imported line (or a ride saved before they existed) lacks instructions or speed limits:
            // ask for them once, the ride works without if it fails.
            if ((route.maneuvers.isEmpty() || route.speedLimits.isEmpty()) && route.track.size >= 2) {
                val needsManeuvers = route.maneuvers.isEmpty()
                _state.update { it.copy(preparing = needsManeuvers) }
                val guide = withTimeoutOrNull(GUIDANCE_TIMEOUT_MILLIS) {
                    suspendRunCatching { guidance.guideAlong(route.track.map { it.point }) }.getOrNull()
                }
                _state.update { it.copy(preparing = false) }
                if (guide != null) {
                    route = route.copy(
                        maneuvers = route.maneuvers.ifEmpty { guide.maneuvers },
                        speedLimits = route.speedLimits.ifEmpty { guide.speedLimits },
                    )
                }
                if (route.maneuvers.isEmpty()) _events.send(NavigationUiEvent.ShowMessage(Res.string.guidance_unavailable))
            }
            val session = NavigationSession(route, request.speedCameras).also { session = it }
            val cameraPoints = request.speedCameras.map { it.point }
            _state.update { it.copy(speedCameras = cameraPoints) }

            if (!request.simulated) device.startBackgroundNavigation(request.name)
            val source = if (request.simulated) RideSimulator(route.track.map { it.point }, SIMULATED_SPEED).source() else device.location
            source.fixes().collect { fix -> onFix(session, fix) }
        }
    }

    private fun onFix(session: NavigationSession, fix: LocationFix) {
        if (startedAt == null) startedAt = now()
        lastRidden?.let { ridden += it.distanceTo(fix.point) }
        lastRidden = fix.point
        followAgainWhenRiding(fix)
        val update = session.update(fix)
        _state.update { it.copy(snapshot = update.snapshot, speedCameraAlerts = settings.speedCameraAlerts.value) }
        update.events.forEach { event ->
            when (event) {
                is NavigationEvent.ConnectorNeeded -> computeConnector(session, event)
                is NavigationEvent.CameraAlert -> if (settings.speedCameraAlerts.value) device.speak(event.spoken())
                is NavigationEvent.StopReached -> viewModelScope.launch {
                    val name = request?.stopNames?.getOrNull(event.stopIndex)
                    _events.send(NavigationUiEvent.ShowMessage(Res.string.stop_reached, name))
                }
                NavigationEvent.Arrived -> arrive()
            }
        }
        notify(update.snapshot)
    }

    /** Map moved by hand or overview: back to following once the rider has ridden on for a while. */
    private fun followAgainWhenRiding(fix: LocationFix) {
        val previous = lastFix
        lastFix = fix
        val state = _state.value
        if (!state.overview && !state.mapMovedByHand) {
            movingAwaySince = null
            return
        }
        // Some fixes have no speed (emulator, some GPS chips): derived from the previous one.
        val speed = fix.speedMetersPerSecond ?: previous?.let {
            val seconds = (fix.timeMillis - it.timeMillis) / 1_000.0
            if (seconds > 0) it.point.distanceTo(fix.point) / seconds else null
        } ?: 0.0
        if (speed <= RECENTER_SPEED) {
            movingAwaySince = null
            return
        }
        val since = movingAwaySince ?: fix.timeMillis.also { movingAwaySince = it }
        if (fix.timeMillis - since >= RECENTER_AFTER_MILLIS) recenter()
    }

    private fun recenter() {
        movingAwaySince = null
        _state.update { it.copy(overview = false, mapMovedByHand = false) }
    }

    private fun computeConnector(session: NavigationSession, need: NavigationEvent.ConnectorNeeded) {
        connectorJob?.cancel()
        connectorJob = viewModelScope.launch {
            // The shortest way back (no motorway, like the ride), starting in the rider's direction.
            suspendRunCatching {
                engine.computeRoute(RouteRequest(listOf(need.from, need.to), RoutePreferences(), RouteStyle.FASTEST, need.heading))
            }
                .onSuccess { connector ->
                    session.useConnector(need.kind, connector, need.targetDistance)
                    _state.update { it.copy(connectorFailed = false) }
                }
                .onFailure {
                    session.connectorFailed()
                    _state.update { it.copy(connectorFailed = true) }
                }
        }
    }

    private fun arrive() {
        val startedAt = startedAt ?: now()
        _state.update { it.copy(summary = RideSummary(ridden, (now() - startedAt) / 1_000.0)) }
        ridingJob?.cancel()
        connectorJob?.cancel()
        device.stopBackgroundNavigation()
    }

    private fun stop() {
        ridingJob?.cancel()
        connectorJob?.cancel()
        if (request?.simulated == false) device.stopBackgroundNavigation()
        activeNavigation.stop()
    }

    /** Keeps the notification short and only rewritten when its text changes. */
    private fun notify(snapshot: NavigationSnapshot) {
        if (request?.simulated != false) return
        val text = when (snapshot.phase) {
            NavigationPhase.OFF_ROUTE -> "Hors itinéraire"
            NavigationPhase.ARRIVED -> "Arrivé"
            else -> snapshot.next?.let { "${it.maneuver.type.notificationLabel()} dans ${roundedDistance(it.distanceMeters)}" }
                ?: "Restant : ${roundedDistance(snapshot.remainingMeters)}"
        }
        if (text == lastNotification) return
        lastNotification = text
        device.updateBackgroundNavigation(text)
    }

    override fun onCleared() {
        if (request?.simulated == false && ridingJob?.isActive == true) device.stopBackgroundNavigation()
    }

    private fun NavigationEvent.CameraAlert.spoken(): String {
        val distance = ((distanceMeters / 50).roundToInt() * 50).coerceAtLeast(50)
        val limit = camera.speedLimitKmh?.let { ", limité à $it" }.orEmpty()
        return "Radar dans $distance mètres$limit"
    }

    private companion object {
        const val ZOOM_STEP = 0.5
        const val MAX_ZOOM_OFFSET = 3.0
        const val GUIDANCE_TIMEOUT_MILLIS = 30_000L

        /** Above 10 km/h for 10 s, the rider is riding again: the map follows them. */
        const val RECENTER_SPEED = 10.0 / 3.6
        const val RECENTER_AFTER_MILLIS = 10_000L

        /** 70 km/h: a brisk ride on winding roads. */
        const val SIMULATED_SPEED = 70.0 / 3.6
    }
}

/** "350 m", "1,2 km": rounded like a GPS says them. */
internal fun roundedDistance(meters: Double): String = when {
    meters < 100 -> "${((meters / 10).roundToInt() * 10).coerceAtLeast(10)} m"
    meters < 1_000 -> "${(meters / 50).roundToInt() * 50} m"
    meters < 10_000 -> {
        val tenths = (meters / 100).roundToInt()
        "${tenths / 10},${tenths % 10} km"
    }
    else -> "${(meters / 1_000).roundToInt()} km"
}

/**
 * Short texts for the Android notification, built outside of Compose (no resource access there).
 */
internal fun ManeuverType.notificationLabel(): String = when (this) {
    ManeuverType.TURN_LEFT, ManeuverType.SHARP_LEFT -> "À gauche"
    ManeuverType.TURN_RIGHT, ManeuverType.SHARP_RIGHT -> "À droite"
    ManeuverType.BEAR_LEFT, ManeuverType.KEEP_LEFT,
    ManeuverType.EXIT_LEFT -> "Serrer à gauche"
    ManeuverType.BEAR_RIGHT, ManeuverType.KEEP_RIGHT,
    ManeuverType.EXIT_RIGHT -> "Serrer à droite"
    ManeuverType.U_TURN -> "Demi-tour"
    ManeuverType.ROUNDABOUT -> "Rond-point"
    ManeuverType.WAYPOINT -> "Étape"
    ManeuverType.ARRIVE -> "Arrivée"
    ManeuverType.FERRY -> "Ferry"
    else -> "Tout droit"
}
